// GC wall API (room-stc, /api/content/*). Spec: docs/superpowers/specs/2026-10-06-gc-wall-posts-design.md
// String ids: docs/superpowers/specs/2026-10-12-clients-on-unified-wall-design.md (3a).
import axios from "axios";
import client from "./client";

export type PostKind = "PHOTO" | "MEDIA";
export type WallItemType = "PHOTO" | "VIDEO" | "YOUTUBE" | "SOUNDCLOUD" | "AUDIO_URL" | "RADIO_STREAM" | "PDF";

/** A post, item or comment id: a ULID from room-stc after the unified-wall cutover, a number before it; always a string here. */
export type ContentId = string;

/** `id` is the user id. */
export interface WallAuthor { id: string; name: string; avatarUrl: string | null }
export interface WallItem extends LinkFacts {
  id: ContentId; position: number; type: WallItemType; url: string | null; externalId: string | null;
  /** file-stc ULIDs. */
  fileId: string | null; thumbnailFileId: string | null; photoUrl: string | null; title: string | null; thumbnailUrl: string | null;
  durationMs: number | null; width: number | null; height: number | null;
  /** v2 PDF items only. */
  pdf?: { pageCount: number | null; sizeBytes: number | null; coverUrl: string | null };
}
/**
 * room-stc 2026-10-24, on every item and link preview: the resolved link provider (`youtube`, `soundcloud`, `stream`;
 * null for photos, videos and rows nobody resolved), the codec hint (null = unknown) and the live flag. Read through
 * the player registry (src/player), never compared here. Optional: older servers send none.
 */
export interface LinkFacts { provider?: string | null; codec?: string | null; live?: boolean }
export interface CaptionFallback { commentId: ContentId; author: WallAuthor; content: string }
export interface WallPost {
  /** USER: the user id; GC: the conversation id as a decimal string. */
  id: ContentId; ownerType: "GC" | "USER"; ownerId: string; kind: PostKind; title: string | null;
  caption: string | null; captionFallback: CaptionFallback | null; author: WallAuthor; items: WallItem[];
  likeCount: number; commentCount: number; likedByMe?: boolean; sourcePostId: ContentId | null;
  /** Only on GET me/uploads: the GC's name, null for a personal post or a missing GC. */
  wallName?: string | null;
  /** Only on GET me/uploads: true when the post was removed from its wall. */
  removed?: boolean;
  createdAt: string; updatedAt: string;
}
export interface Page<T> { items: T[]; nextCursor: string | null }
export interface WallComment {
  id: ContentId; postId: ContentId; author: WallAuthor; content: string; createdAt: string; editedAt: string | null;
}
export interface LinkPreview extends LinkFacts {
  type: WallItemType; url: string; externalId: string | null; title: string | null; thumbnailUrl: string | null;
}
export interface WallLike { likeCount: number; likedByMe: boolean }
export interface WallCounts { postId: ContentId; likeCount: number; commentCount: number; captionFallback: CaptionFallback | null }
/** WALL_POST_DELETED `{postId}` and WALL_COMMENT_DELETED `{postId, commentId}`. */
export interface WallDeleted { postId: ContentId; commentId?: ContentId }
export type PostItemInput = { url: string; title?: string } | { fileId: string; thumbnailFileId?: string; title?: string };
export interface PostInput {
  kind: PostKind; title?: string; caption?: string; items: PostItemInput[];
  /** Import: the ULID of the caller's post this one copies. Create only. */
  sourcePostId?: string;
}

/** Whose wall a post is on (spec 3b §2): `post.ownerType` + `post.ownerId`. */
export type WallRef = { type: "gc"; convId: number } | { type: "user"; userId: string };
/** World lists of one post type (v2/posts): MapTxat Personal page News (`onMaptxat`) and Personal (WALL). */
export type WorldPostType = "NEWS" | "WALL" | "APOPILO" | "BOOK";
/**
 * A feed the client can list: a GC wall, a user's wall, my wall, the following feed, a GC wall search, my liked posts,
 * the General Wall, a World list of one post type, the World agenda (upcoming events, soonest first), or a World search
 * over the posts of every USER wall (`postType` null = every type).
 */
export type WallFeedSource =
  | WallRef | { type: "me" } | { type: "following" } | { type: "gcSearch"; convId: number; q: string } | { type: "liked" } | { type: "general" }
  | { type: "world"; postType: WorldPostType; onMaptxat?: boolean; kind?: ApopiloKind } | { type: "worldAgenda" }
  | { type: "worldSearch"; q: string; postType: PostType | null };
export const wallRefOf = (p: Pick<WallPost, "ownerType" | "ownerId">): WallRef =>
  p.ownerType === "GC" ? { type: "gc", convId: Number(p.ownerId) } : { type: "user", userId: p.ownerId };

export function wallPath(s: WallFeedSource): string {
  switch (s.type) {
    case "gc":
      return `/api/content/gc/${s.convId}/posts`;
    case "user":
      return `/api/content/users/${s.userId}/posts`;
    case "me":
      return "/api/content/users/me/posts";
    case "following":
      return "/api/content/feed/following";
    case "gcSearch":
      return `/api/content/gc/${s.convId}/posts`;
    case "liked":
      return "/api/content/me/liked";
    case "general":
      return "/api/content/v2/feed/general";
    case "world":
      return "/api/content/v2/posts";
    case "worldAgenda":
      return "/api/content/v2/events";
    case "worldSearch":
      return "/api/content/v2/search/posts";
  }
}

/** Query params a feed source adds to cursor + limit. */
function wallParams(s: WallFeedSource): Record<string, string | boolean> {
  switch (s.type) {
    case "gcSearch":
      return { q: s.q };
    case "world":
      return worldParams(s.postType, s.onMaptxat, s.kind);
    case "worldAgenda":
      return { when: "upcoming" };
    case "worldSearch":
      return s.postType ? { q: s.q, type: s.postType } : { q: s.q };
    default:
      return {};
  }
}

const worldParams = (type: PostType, onMaptxat?: boolean, kind?: ApopiloKind): Record<string, string | boolean> => ({
  type, ...(onMaptxat ? { onMaptxat: true } : {}), ...(kind ? { kind } : {}),
});

const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/** A post link id is a ULID; anything else (numeric legacy ids included) is not a post id. */
export function parsePostId(s: string | null | undefined): string | null {
  return s && ULID_RE.test(s) ? s : null;
}

/** Server codes the UI has a string for (contentErrorText); anything else is UNKNOWN. */
export const KNOWN_CONTENT_CODES = [
  "FORBIDDEN", "NOT_FOUND", "UNSUPPORTED_LINK", "LINK_NOT_EMBEDDABLE", "LINK_UNREACHABLE", "RADIO_NOT_LAST",
  "TOO_MANY_ITEMS", "EMPTY_POST", "KIND_MISMATCH", "PHOTO_NOT_UPLOADED", "INVALID_FIELD", "PREVIEW_ONLY_LINK",
  "BOOK_PDF_NOT_PRIVATE", "BOOK_NOT_FOR_SALE", "OWN_BOOK", "SUPPORT_NEEDS_PURCHASE", "ALREADY_OWNED",
  "SUPPORT_AMOUNT_INVALID", "BOOK_NEEDS_ONE_PDF", "TYPE_NOT_ALLOWED", "BOOK_LOCKED", "PAYMENTS_UNAVAILABLE", "PAYMENT_DECLINED",
  "APOPILO_DATES_INVALID", "APOPILO_OWN_OFFER", "APOPILO_KIND_FIXED", "APOPILO_SCORE_INVALID", "APOPILO_NOT_OPEN", "APOPILO_STARTED",
  "APOPILO_BALANCE_NEGATIVE", "APOPILO_BALANCE_LOW", "APOPILO_REQUEST_NOT_WAITING", "APOPILO_STAY_NOT_ENDED", "APOPILO_ALREADY_RATED",
  "APOPILO_TAKEN", "APOPILO_HAS_REQUESTS",
] as const;
const KNOWN = new Set<string>(KNOWN_CONTENT_CODES);

/** A failed wall request; `code` is the server's machine code (e.g. RADIO_NOT_LAST) or UNKNOWN. */
export class ContentError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number) {
    super(code);
    this.name = "ContentError";
    this.code = code;
    this.status = status;
  }
}

/** room-stc answers INVALID_FIELD for a cursor minted before the unified-wall cutover: the list restarts (spec 3a). */
export const isStaleCursor = (e: unknown): boolean => e instanceof ContentError && e.code === "INVALID_FIELD";

/** World search refusals (room-stc v2/search/posts): too many searches (429 RATE_LIMITED) or OpenSearch down (503). */
export type SearchRefusal = "rateLimited" | "unavailable";
export const searchRefusalOf = (e: unknown): SearchRefusal | null =>
  e instanceof ContentError ? (e.status === 429 ? "rateLimited" : e.status === 503 ? "unavailable" : null) : null;

// Id normalization. Temporary: room-stc sends numbers until the cutover; removed once prod has run it
// (spec 3a, rollout step 7). Hooks and components only ever see string ids.
type Raw = Record<string, unknown>;

function asRaw(v: unknown): Raw {
  if (typeof v !== "object" || v === null) throw new TypeError(`wall payload must be an object, got ${JSON.stringify(v)}`);
  return v as Raw;
}

export function toId(v: unknown): ContentId {
  if (typeof v === "string" && v !== "") return v;
  if (typeof v === "number" && Number.isSafeInteger(v)) return String(v);
  throw new TypeError(`wall id must be a string or an integer, got ${JSON.stringify(v)}`);
}

const toIdOrNull = (v: unknown): ContentId | null => (v == null ? null : toId(v));

function normalizeItem(v: unknown): WallItem {
  const r = asRaw(v);
  return { ...(r as unknown as WallItem), id: toId(r.id) };
}

function normalizeFallback(v: unknown): CaptionFallback | null {
  if (v == null) return null;
  const r = asRaw(v);
  return { ...(r as unknown as CaptionFallback), commentId: toId(r.commentId) };
}

export function normalizePost(v: unknown): WallPost {
  const r = asRaw(v);
  // v2 posts that carry a `type` have `description` (no `caption`) and no `kind` outside WALL: the card reads both.
  const typed = typeof r.type === "string";
  return {
    ...(r as unknown as WallPost),
    ...(typed ? { caption: (r.caption as string | null | undefined) ?? (r.description as string | null | undefined) ?? null, kind: (r.kind as PostKind | undefined) ?? "MEDIA" } : {}),
    id: toId(r.id),
    sourcePostId: toIdOrNull(r.sourcePostId),
    captionFallback: normalizeFallback(r.captionFallback),
    items: Array.isArray(r.items) ? r.items.map(normalizeItem) : [],
  };
}

export function normalizeComment(v: unknown): WallComment {
  const r = asRaw(v);
  return { ...(r as unknown as WallComment), id: toId(r.id), postId: toId(r.postId) };
}

export function normalizeCounts(v: unknown): WallCounts {
  const r = asRaw(v);
  return { ...(r as unknown as WallCounts), postId: toId(r.postId), captionFallback: normalizeFallback(r.captionFallback) };
}

export function normalizeDeleted(v: unknown): WallDeleted {
  const r = asRaw(v);
  return r.commentId == null ? { postId: toId(r.postId) } : { postId: toId(r.postId), commentId: toId(r.commentId) };
}

export function normalizePage<T>(item: (v: unknown) => T): (v: unknown) => Page<T> {
  return (v) => {
    const r = asRaw(v);
    return { items: Array.isArray(r.items) ? r.items.map(item) : [], nextCursor: typeof r.nextCursor === "string" ? r.nextCursor : null };
  };
}

export async function call<T>(req: Promise<{ data: { result: unknown } }>, normalize?: (raw: unknown) => T): Promise<T> {
  let result: unknown;
  try {
    result = (await req).data.result;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const msg = (e.response?.data as { message?: unknown } | undefined)?.message;
      const code = typeof msg === "string" && KNOWN.has(msg) ? msg : "UNKNOWN";
      throw new ContentError(code, e.response?.status ?? 0);
    }
    throw e;
  }
  if (!normalize) return result as T;
  try {
    return normalize(result);
  } catch {
    // A payload the client cannot read is the generic load error (spec 3a errors table).
    throw new ContentError("UNKNOWN", 200);
  }
}

export const getWall = (source: WallFeedSource, cursor?: string | null, limit = 20) =>
  call(
    client.get(wallPath(source), {
      params: { ...wallParams(source), cursor: cursor || undefined, limit },
    }),
    normalizePage(normalizePost),
  );

/** Which wall the picked post must fit (spec 4 §1.1). The web never asks for CAST. */
/** room-stc `UploadFit`: CAST lists any post with a queueable item (Android UploadsMode.Cast). */
export type UploadFit = "GC" | "USER" | "CAST";

/** The caller's authored posts on any wall, removed ones included, newest first. */
export const getUploads = (fit: UploadFit, cursor?: string | null, limit = 20) =>
  call(client.get("/api/content/me/uploads", { params: { for: fit, cursor: cursor || undefined, limit } }), normalizePage(normalizePost));

export const createOnMyWall = (input: PostInput) => call(client.post("/api/content/users/me/posts", input), normalizePost);

export const getPost = (id: ContentId) => call(client.get(`/api/content/posts/${id}`), normalizePost);

export const resolveLink = (url: string) => call<LinkPreview>(client.post("/api/content/link/resolve", { url }));

export const createPost = (convId: number, input: PostInput) =>
  call(client.post(`/api/content/gc/${convId}/posts`, input), normalizePost);

export const updatePost = (id: ContentId, input: PostInput) => call(client.put(`/api/content/posts/${id}`, input), normalizePost);

export const deletePost = async (id: ContentId) => {
  await call<boolean>(client.delete(`/api/content/posts/${id}`));
};

export const likePost = (id: ContentId) => call<WallLike>(client.put(`/api/content/posts/${id}/like`));

export const unlikePost = (id: ContentId) => call<WallLike>(client.delete(`/api/content/posts/${id}/like`));

export const getComments = (postId: ContentId, cursor?: string | null, limit = 20) =>
  call(client.get(`/api/content/posts/${postId}/comments`, { params: { cursor: cursor || undefined, limit } }), normalizePage(normalizeComment));

export const addComment = (postId: ContentId, content: string) =>
  call(client.post(`/api/content/posts/${postId}/comments`, { content }), normalizeComment);

export const editComment = (id: ContentId, content: string) =>
  call(client.put(`/api/content/comments/${id}`, { content }), normalizeComment);

export const deleteComment = async (id: ContentId) => {
  await call<boolean>(client.delete(`/api/content/comments/${id}`));
};

// Personal page (room-stc, spec 2026-10-26-personal-page-step1-design.md).
export type PostType = "WALL" | "EVENT" | "BOOK" | "NEWS" | "BLOG" | "APOPILO";
export type NewsInfo = { showOnMaptxat: boolean };
export interface EventLocation { lat: number; lng: number; placeName: string | null }
export type EventJoinMe = WallAuthor;
/** Event facts on a v2 post. Instants are ISO-8601 UTC; `timezone` is the event's IANA zone label. */
export interface EventInfo {
  startsAt: string; endsAt: string | null; timezone: string; priceCents: number | null; currency: string;
  ticketUrl: string | null; live: boolean; joinMe: EventJoinMe; location: EventLocation | null; passed: boolean;
  /** True when the Join me account no longer exists: show "Join me · unavailable" instead of the button. */
  joinMeUnavailable?: boolean;
}
/** Create/update body; Join me is a pasted profile link (empty/absent = me). */
export interface EventInput {
  startsAt: string; endsAt?: string | null; timezone: string; priceCents?: number | null; currency?: string | null;
  ticketUrl?: string | null; live: boolean; joinMeProfileUrl?: string | null;
  location?: { lat: number; lng: number; placeName?: string | null };
}
/** Book facts on a v2 post (room-stc ContentBook). `canRead` is the server's verdict: author, BOUGHT viewer, or a free book. */
export interface BookInfo {
  coverUrl: string | null;
  /** The author's own cover file (null = the PDF's first page); an edit sends it back or room-stc drops the cover. */
  coverFileId?: string | null;
  authorName?: string; language?: string; pages?: number;
  original?: { author?: string; year?: number; language?: string };
  translation?: { translator?: string; year?: number; language?: string };
  recommendedAge?: string; priceCents: number | null; currency: string; shopUrl?: string; supportUrl?: string;
  owned: "BOUGHT" | "SUPPORTED" | null; ownedAt: string | null; canRead: boolean;
}
export type ApopiloKind = "TUTOR" | "APOPILO";
export type ApopiloViewerStatus = "OPEN" | "TAKE" | "REQUESTED" | "IN_PROGRESS" | "TAKEN" | "ENDED" | "ON_HOLD";
export interface ApopiloReview { reviewer: WallAuthor; score: number; review: string; createdAt: string }
/** Apopilo offer facts on a v2 post (room-stc ContentApopilo); dates are YYYY-MM-DD. balanceAfter / enough: the take projection, null on a started offer or for the author. */
export interface ApopiloInfo {
  kind: ApopiloKind; startsOn: string; endsOn: string; units: number;
  pricePerDayCents: number | null; totalCents: number | null; currency: string;
  status: string; viewerStatus: ApopiloViewerStatus; started: boolean; place: EventLocation | null; requestCount: number;
  myRequest: { id: string; status: string } | null;
  stay: { id: string; status: string; role: "PROVIDER" | "GUEST"; startsOn: string; canCancel: boolean; ratedByMe: boolean } | null;
  reviews: ApopiloReview[]; balanceAfter: number | null; enough: boolean | null;
}
/** Create/update body: kind is fixed after create; place may be null. */
export interface ApopiloInput { kind: ApopiloKind; startsOn: string; endsOn: string; place: EventLocation | null }
/** Create/update body: the writable BookInfo fields; `coverFileId` = a READY image of the author's (absent = PDF first page). */
export type BookInput = Omit<BookInfo, "owned" | "ownedAt" | "canRead" | "coverUrl" | "coverFileId"> & { coverFileId?: string };
/** v2 post view: the v1 fields plus type, description and the viewer's save state. */
export interface PostV2 extends WallPost {
  type?: PostType; description?: string | null; savedByMe?: boolean;
  news?: NewsInfo; event?: EventInfo; book?: BookInfo; apopilo?: ApopiloInfo;
  /** Author only. */
  saveCount?: number;
}
export interface PersonalPageInfo { pageId: string; userId: string; personalPostCount: number; savedCount?: number }
export interface TypeCounts { wall: number; event: number; book: number; news: number; blog: number; apopilo: number }

export const savePost = (id: ContentId) => call<{ saved: boolean }>(client.put(`/api/content/posts/${id}/save`));

export const unsavePost = (id: ContentId) => call<{ saved: boolean }>(client.delete(`/api/content/posts/${id}/save`));

export const getSaved = (cursor?: string | null, limit = 20) =>
  call(client.get("/api/content/v2/me/saved", { params: { cursor: cursor || undefined, limit } }), normalizePage(normalizePost) as (v: unknown) => Page<PostV2>);

export const getLiked = (cursor?: string | null, limit = 20) =>
  call(client.get("/api/content/v2/me/liked", { params: { cursor: cursor || undefined, limit } }), normalizePage(normalizePost) as (v: unknown) => Page<PostV2>);

export const getPersonalPage = (userId: string | "me") =>
  call<PersonalPageInfo>(client.get(`/api/content/users/${userId}/page`));

/** A user's posts of one type (v2). */
export const getUserPostsByType = (userId: string, type: PostType, cursor?: string | null, limit = 20) =>
  call(
    client.get(`/api/content/v2/users/${userId}/posts`, { params: { type, cursor: cursor || undefined, limit } }),
    normalizePage(normalizePost) as (v: unknown) => Page<PostV2>,
  );

/** The Personal grid: a user's WALL-type posts. */
export const getPersonalPosts = (userId: string, cursor?: string | null, limit = 20) =>
  getUserPostsByType(userId, "WALL", cursor, limit);

export type PostInputV2 = { type: PostType; title?: string; description?: string; items: PostItemInput[]; news?: NewsInfo; event?: EventInput; book?: BookInput; apopilo?: ApopiloInput };

export const createPostV2 = (input: PostInputV2) =>
  call(client.post("/api/content/v2/users/me/posts", input), normalizePost as (v: unknown) => PostV2);

export const getPostV2 = (id: ContentId) => call(client.get(`/api/content/v2/posts/${id}`), normalizePost as (v: unknown) => PostV2);

export const deletePostV2 = async (id: ContentId) => {
  await call<unknown>(client.delete(`/api/content/v2/posts/${id}`));
};

/** True for a v2 post of a non-WALL type: v1 post routes are WALL-only (room-stc ScopeWall) and answer NOT_FOUND. */
export const isTypedPost = (p: { type?: string | null }): boolean => p.type != null && p.type !== "WALL";

export const getTypeCounts = (userId: string) => call<TypeCounts>(client.get(`/api/content/v2/users/${userId}/counts`));

export const updatePostV2 = (id: ContentId, input: PostInputV2) =>
  call(client.put(`/api/content/v2/posts/${id}`, input), normalizePost as (v: unknown) => PostV2);

export const createEvent = (input: Omit<PostInputV2, "type"> & { event: EventInput }) => createPostV2({ ...input, type: "EVENT" });

const pageOf = normalizePage(normalizePost) as (v: unknown) => Page<PostV2>;

/**
 * Every user's posts of one type (World), newest first. `onMaptxat` (NEWS only, room-stc 4739452): only News whose
 * author ticked "Show on MapTxat". WALL = personal posts on USER walls (GC posts excluded).
 */
export const getWorldPosts = (type: PostType, opts: { onMaptxat?: boolean; kind?: ApopiloKind } = {}, cursor?: string | null, limit = 20) =>
  call(client.get("/api/content/v2/posts", { params: { ...worldParams(type, opts.onMaptxat, opts.kind), cursor: cursor || undefined, limit } }), pageOf);

/** Event Wall: every user's events (World). */
export const getWorldEvents = (cursor?: string | null, limit = 20) => getWorldPosts("EVENT", {}, cursor, limit);

export const getUserEvents = (userId: string, cursor?: string | null, limit = 20) =>
  getUserPostsByType(userId, "EVENT", cursor, limit);

export type AgendaWhen = "upcoming" | "past";

/** Agenda: events ordered by start time, for one user or the World. */
export const getAgenda = (source: { userId: string } | "world", when: AgendaWhen, cursor?: string | null, limit = 20) =>
  call(
    client.get(source === "world" ? "/api/content/v2/events" : `/api/content/v2/users/${source.userId}/events`,
      { params: { when, cursor: cursor || undefined, limit } }),
    pageOf,
  );

export interface EventMapBounds { lat1: number; lng1: number; lat2: number; lng2: number }

/** Events with a place inside a map viewport. */
export const getEventMap = (b: EventMapBounds) =>
  call(client.get("/api/content/v2/events/map", { params: { lat1: b.lat1, lng1: b.lng1, lat2: b.lat2, lng2: b.lng2 } }), pageOf);

/** Sets or moves an event's place (author only). */
export const putEventLocation = (id: ContentId, loc: { lat: number; lng: number; placeName: string | null }) =>
  call(client.put(`/api/content/posts/${id}/location`, loc), normalizePost as (v: unknown) => PostV2);

/** Removes an event's place; the post stays. */
export const deleteEventLocation = (id: ContentId) =>
  call(client.delete(`/api/content/posts/${id}/location`), normalizePost as (v: unknown) => PostV2);
